import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Asset, useListAssetsQuery } from "features/trading/accounts";
import {
  HeldPeriod,
  serializeHeldPeriods,
} from "features/trading/heldPeriods";
import { SelectedAsset } from "helpers/types";
import { useDebounce } from "use-debounce";

const MIN_SEARCH_LENGTH = 2;
const ASSET_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_PLACEHOLDER = "Search by symbol or name...";
const DEFAULT_LABEL = "Asset";

export const getAssetLabel = (asset: Pick<Asset, "symbol" | "name">) =>
  `${asset.symbol} - ${asset.name}`;

const toSelectedAsset = ({ id, symbol, name }: Asset): SelectedAsset => ({
  id,
  symbol,
  name,
});

const getNoOptionsText = (
  trimmedSearch: string,
  hasHeldContext: boolean,
  hasPeriods: boolean,
) => {
  if (!hasHeldContext) {
    return trimmedSearch.length >= MIN_SEARCH_LENGTH
      ? "No assets found"
      : "Type at least 2 characters to search";
  }
  // With an account context the list is RESTRICTED to what that account
  // held, so an empty result has two quite different causes and they must
  // not share a sentence: nothing SEARCHED FOR is in the restriction, or
  // the restriction itself is empty. Blaming the search for the second
  // sends the user hunting a spelling mistake that isn't there — and
  // blaming the holdings for the first denies securities they can see.
  if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
    return hasPeriods
      ? "No security held in the period matches your search"
      : "No security the account holds matches your search";
  }
  return hasPeriods
    ? "The account held no securities in the selected period"
    : "The account holds no securities";
};

// One security is one option however many rows carried it here — a tax lot
// per snapshot day, a held row that the search also matched. The backend
// already answers per asset id; this is the last guard before the menu.
const uniqueById = (assets: Asset[]) => {
  const seen = new Set<number>();
  return assets.filter((asset) => {
    if (seen.has(asset.id)) return false;
    seen.add(asset.id);
    return true;
  });
};

const useAssetSearch = (
  heldByAccountId?: number,
  heldPeriods?: HeldPeriod[],
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearchTerm.trim();
  const hasSearch = trimmedSearch.length >= MIN_SEARCH_LENGTH;
  const heldPeriodsParam = useMemo(
    () => serializeHeldPeriods(heldPeriods),
    [heldPeriods],
  );

  // An account context RESTRICTS this picker to the securities that account
  // held — over the reporting periods when it has them, otherwise what it
  // holds now. Both queries below therefore carry held_only: a search that
  // reached outside the restriction would offer a security the report
  // cannot account for.
  const heldFilter = useMemo(
    () =>
      heldByAccountId
        ? {
            held_by_account: heldByAccountId,
            held_only: true,
            ...(heldPeriodsParam ? { held_periods: heldPeriodsParam } : {}),
          }
        : undefined,
    [heldByAccountId, heldPeriodsParam],
  );

  // With an account context the dropdown is useful before any typing: it
  // opens with the securities that account held.
  const heldQuery = useListAssetsQuery(
    { ...heldFilter, page_size: ASSET_PAGE_SIZE },
    { skip: !heldFilter },
  );

  // Typing searches every asset only when there is no account context;
  // with one it searches WITHIN the restricted set.
  const searchQuery = useListAssetsQuery(
    {
      search: trimmedSearch,
      page_size: ASSET_PAGE_SIZE,
      ...heldFilter,
    },
    { skip: !hasSearch },
  );

  const results = useMemo(() => {
    if (hasSearch) return uniqueById(searchQuery.data?.results ?? []);
    if (!heldFilter) return [];
    const held = uniqueById(heldQuery.data?.results ?? []);
    // 1 typed character: below the search threshold, narrow the held list
    // locally so the dropdown stays responsive.
    const needle = trimmedSearch.toLowerCase();
    if (!needle) return held;
    return held.filter(
      (asset) =>
        asset.symbol.toLowerCase().includes(needle) ||
        asset.name.toLowerCase().includes(needle),
    );
  }, [hasSearch, heldFilter, trimmedSearch, searchQuery.data, heldQuery.data]);

  const loading = hasSearch
    ? searchQuery.isLoading || searchQuery.isFetching
    : !!heldFilter && (heldQuery.isLoading || heldQuery.isFetching);

  return {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext: !!heldByAccountId,
    hasHeldPeriods: !!heldPeriodsParam,
  };
};

interface BaseAssetAutocompleteProps {
  label?: string;
  placeholder?: string;
  // When set, the picker is RESTRICTED to the securities this account held:
  // nothing outside that set is listed, and typing searches within it.
  heldByAccountId?: number;
  // The reporting periods the restriction is measured over. Without them it
  // falls back to what the account holds now, which is all a screen with no
  // reporting period can ask. Ignored unless heldByAccountId is set.
  heldPeriods?: HeldPeriod[];
}

interface SingleAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple?: false;
  value: number | "";
  onChange: (assetId: number | "") => void;
  required?: boolean;
  // A short note shown at the end of an option's row (e.g. "In Bucket 1").
  // Informational only: the option stays selectable.
  getOptionNote?: (asset: Asset) => string | undefined;
}

interface MultiAssetAutocompleteProps extends BaseAssetAutocompleteProps {
  multiple: true;
  value: SelectedAsset[];
  onChange: (assets: SelectedAsset[]) => void;
}

type AssetAutocompleteProps =
  | SingleAssetAutocompleteProps
  | MultiAssetAutocompleteProps;

const LoadingAdornment = ({ loading }: { loading: boolean }) =>
  loading ? <CircularProgress color="inherit" size={20} /> : null;

function SingleAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
  required = false,
  heldByAccountId,
  heldPeriods,
  getOptionNote,
}: SingleAssetAutocompleteProps) {
  const {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext,
    hasHeldPeriods,
  } = useAssetSearch(heldByAccountId, heldPeriods);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const options = useMemo(() => {
    if (selectedAsset && !results.some((a) => a.id === selectedAsset.id)) {
      return [selectedAsset, ...results];
    }
    return results;
  }, [results, selectedAsset]);

  useEffect(() => {
    if (value === "") {
      setSelectedAsset(null);
      setSearchTerm("");
    }
  }, [value, setSearchTerm]);

  const clearSelection = () => {
    setSelectedAsset(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <Autocomplete
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={selectedAsset}
      loading={loading}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          setSearchTerm(newValue);
          if (selectedAsset) clearSelection();
        } else if (reason === "clear") {
          clearSelection();
        }
      }}
      onChange={(_, asset) => {
        setSelectedAsset(asset);
        onChange(asset?.id ?? "");
        if (asset) setSearchTerm(getAssetLabel(asset));
      }}
      renderOption={(optionProps, option) => {
        const note = getOptionNote?.(option);
        return (
          <li {...optionProps} key={option.id}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Box flexGrow={1} minWidth={0}>
                {getAssetLabel(option)}
              </Box>
              {note && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  flexShrink={0}
                  whiteSpace="nowrap"
                >
                  {note}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(
        trimmedSearch,
        hasHeldContext,
        hasHeldPeriods,
      )}
    />
  );
}

function MultiAssetAutocomplete({
  value,
  onChange,
  label = DEFAULT_LABEL,
  placeholder = DEFAULT_PLACEHOLDER,
  heldByAccountId,
  heldPeriods,
}: MultiAssetAutocompleteProps) {
  const {
    searchTerm,
    setSearchTerm,
    trimmedSearch,
    results,
    loading,
    hasHeldContext,
    hasHeldPeriods,
  } = useAssetSearch(heldByAccountId, heldPeriods);

  const options = useMemo<SelectedAsset[]>(() => {
    const resultsAsSelected: SelectedAsset[] = results.map(toSelectedAsset);
    const resultIds = new Set(resultsAsSelected.map((r) => r.id));
    const missing = value.filter((v) => !resultIds.has(v.id));
    return [...missing, ...resultsAsSelected];
  }, [results, value]);

  const normalize = (assets: SelectedAsset[]): SelectedAsset[] =>
    assets.map(({ id, symbol, name }) => ({ id, symbol, name }));

  return (
    <Autocomplete<SelectedAsset, true>
      multiple
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={getAssetLabel}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      value={value}
      inputValue={searchTerm}
      loading={loading}
      onInputChange={(_, next, reason) => {
        if (reason === "input") setSearchTerm(next);
        else if (reason === "clear" || reason === "reset") setSearchTerm("");
      }}
      onChange={(_, assets) => {
        onChange(normalize(assets));
        setSearchTerm("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <LoadingAdornment loading={loading} />
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={getNoOptionsText(
        trimmedSearch,
        hasHeldContext,
        hasHeldPeriods,
      )}
    />
  );
}

export default function AssetAutocomplete(props: AssetAutocompleteProps) {
  return props.multiple ? (
    <MultiAssetAutocomplete {...props} />
  ) : (
    <SingleAssetAutocomplete {...props} />
  );
}
